/**
 * Tariffs-spezifische API-Funktionen (EEG/PPA/Energieerlöse)
 * Vergütungstarife und Energieerlöse für Windpark-Projekte
 */
import { api } from './client';
import {
  EEGTariff, EEGTariffUpdate,
  MarktpreisTariff, MarktpreisTariffUpdate,
  PPATariff, PPATariffCreate, PPATariffUpdate,
  PPATariffResponse, PPATariffDeleteResponse,
  EnergyRevenuesResponse
} from '../../types';

export const tariffsApi = {
  // EEG-Tarif Funktionen

  /**
   * Holt den EEG-Tarif eines Projekts
   * Gibt null zurück wenn kein EEG-Tarif vorhanden ist
   */
  async getEEGTariff(projectId: string): Promise<EEGTariff | null> {
    return await api.get(`/projects/${projectId}/eeg-tariff`);
  },

  async createOrUpdateEEGTariff(projectId: string, data: EEGTariffUpdate): Promise<EEGTariff> {
    return api.post(`/projects/${projectId}/eeg-tariff`, data);
  },

  async updateEEGTariff(projectId: string, data: EEGTariffUpdate): Promise<EEGTariff> {
    return api.put(`/projects/${projectId}/eeg-tariff`, data);
  },

  // Marktpreis-Tarif Funktionen

  /**
   * Holt den Marktpreis-Tarif eines Projekts
   * Gibt null zurück wenn kein Marktpreis-Tarif vorhanden ist
   */
  async getMarktpreisTariff(projectId: string): Promise<MarktpreisTariff | null> {
    try {
      return await api.get(`/projects/${projectId}/marktpreis-tariff`);
    } catch (error: any) {
      // 404 bedeutet: kein Marktpreis-Tarif vorhanden
      if (error.message?.includes('404')) {
        return null;
      }
      throw error;
    }
  },

  async createOrUpdateMarktpreisTariff(projectId: string, data: MarktpreisTariffUpdate): Promise<MarktpreisTariff> {
    return api.post(`/projects/${projectId}/marktpreis-tariff`, data);
  },

  async updateMarktpreisTariff(projectId: string, data: MarktpreisTariffUpdate): Promise<MarktpreisTariff> {
    return api.put(`/projects/${projectId}/marktpreis-tariff`, data);
  },

  // PPA-Tarif Funktionen

  async getPPATariffs(projectId: string): Promise<PPATariff[]> {
    return api.get(`/projects/${projectId}/ppa-tariffs`);
  },

  /**
   * Erstellt einen neuen PPA-Tarif für ein Projekt.
   * 
   * Gibt neben dem erstellten PPA auch domänenübergreifende Validierungen zurück.
   * Diese können verwendet werden, um Warnungen im UI anzuzeigen
   * (z.B. wenn der PPA außerhalb der Projektlaufzeit liegt).
   */
  async createPPATariff(projectId: string, data: PPATariffCreate): Promise<PPATariffResponse> {
    return api.post(`/projects/${projectId}/ppa-tariffs`, data);
  },

  /**
   * Aktualisiert einen PPA-Tarif.
   * 
   * Gibt neben dem aktualisierten PPA auch domänenübergreifende Validierungen zurück.
   * Diese können verwendet werden, um Warnungen im UI anzuzeigen.
   */
  async updatePPATariff(projectId: string, tariffId: string, data: PPATariffUpdate): Promise<PPATariffResponse> {
    return api.put(`/projects/${projectId}/ppa-tariffs/${tariffId}`, data);
  },

  /**
   * Löscht einen PPA-Tarif.
   * 
   * Gibt nach dem Löschen domänenübergreifende Validierungen zurück.
   * Diese können verwendet werden, um den Warning-Status im UI zu aktualisieren.
   */
  async deletePPATariff(projectId: string, tariffId: string): Promise<PPATariffDeleteResponse> {
    return api.delete(`/projects/${projectId}/ppa-tariffs/${tariffId}`);
  },

  // Energieerlöse Funktionen

  /**
   * Holt die berechneten Energieerlöse eines Projekts
   * Gibt null zurück wenn keine Energieerlöse vorhanden sind
   */
  async getEnergyRevenues(projectId: string): Promise<EnergyRevenuesResponse | null> {
    return await api.get(`/projects/${projectId}/energy-revenues`);
  },

  /**
   * Berechnet die Energieerlöse für ein Projekt und speichert sie
   */
  async calculateEnergyRevenues(projectId: string): Promise<EnergyRevenuesResponse> {
    return api.post(`/projects/${projectId}/energy-revenues/calculate`, {});
  },
};

